import React, { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';
import { InventoryStatus } from '../../../types/inventory';
import { mockInventoryService } from '../../../services/mockInventoryService';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { confirmDelete, swalError } from '../../common/confirm';

interface InventoryStatusesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  status_code: string;
  status_name: string;
  status_description: string;
  status_color: string;
  is_active_state: boolean;
  display_order: number;
  is_active: boolean;
}

interface FormErrors {
  status_code?: string;
  status_name?: string;
  status_description?: string;
  status_color?: string;
  display_order?: string;
}

const initialFormData: FormData = {
  status_code: '',
  status_name: '',
  status_description: '',
  status_color: '#007bff',
  is_active_state: true,
  display_order: 1,
  is_active: true
};

const InventoryStatusesModal: React.FC<InventoryStatusesModalProps> = ({ isOpen, onClose }) => {
  const [statuses, setStatuses] = useState<InventoryStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStatus, setEditingStatus] = useState<InventoryStatus | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await mockInventoryService.getStatuses();
      setStatuses(response.data);
    } catch (error) {
      console.error('Error loading inventory statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingStatus(null);
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  const handleAddClick = () => {
    setEditingStatus(null);
    setFormData(initialFormData);
    setErrors({});
    setShowForm(true);
  };

  const handleEditClick = (status: InventoryStatus) => {
    setEditingStatus(status);
    setFormData({
      status_code: status.status_code,
      status_name: status.status_name,
      status_description: status.status_description || '',
      status_color: status.status_color || '#007bff',
      is_active_state: status.is_active_state,
      display_order: status.display_order,
      is_active: status.is_active
    });
    setErrors({});
    setShowForm(true);
  };

  const handleDeleteClick = async (status: InventoryStatus) => {
    const ok = await confirmDelete({ title: `Delete status "${status.status_name}"?` });
    if (!ok) return;
    try {
      await mockInventoryService.deleteStatus(status.id);
      await loadData();
    } catch (error) {
      console.error('Error deleting inventory status:', error);
      swalError('Failed to delete status. Please try again.');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.status_code.trim()) {
      newErrors.status_code = 'Status code is required';
    } else if (formData.status_code.length > 50) {
      newErrors.status_code = 'Status code must be 50 characters or less';
    }

    if (!formData.status_name.trim()) {
      newErrors.status_name = 'Status name is required';
    } else if (formData.status_name.length > 100) {
      newErrors.status_name = 'Status name must be 100 characters or less';
    }

    if (!formData.status_description.trim()) {
      newErrors.status_description = 'Description is required';
    } else if (formData.status_description.length > 500) {
      newErrors.status_description = 'Description must be 500 characters or less';
    }

    if (formData.status_color && !/^#[0-9A-Fa-f]{6}$/.test(formData.status_color)) {
      newErrors.status_color = 'Please enter a valid hex color code';
    }

    if (formData.display_order < 1) {
      newErrors.display_order = 'Display order must be greater than 0';
    }

    // Check for duplicate codes/names (excluding current item when editing)
    const existingStatus = statuses.find(s =>
      (s.status_code.toLowerCase() === formData.status_code.toLowerCase() ||
       s.status_name.toLowerCase() === formData.status_name.toLowerCase()) &&
      s.id !== editingStatus?.id
    );
    if (existingStatus) {
      if (existingStatus.status_code.toLowerCase() === formData.status_code.toLowerCase()) {
        newErrors.status_code = 'A status with this code already exists';
      }
      if (existingStatus.status_name.toLowerCase() === formData.status_name.toLowerCase()) {
        newErrors.status_name = 'A status with this name already exists';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const requestData = {
        status_code: formData.status_code.trim(),
        status_name: formData.status_name.trim(),
        status_description: formData.status_description.trim() || undefined,
        status_color: formData.status_color || undefined,
        is_active_state: formData.is_active_state,
        display_order: formData.display_order,
        is_active: formData.is_active,
        created_by: 1 // TODO: Get from auth context
      };

      if (editingStatus) {
        await mockInventoryService.updateStatus(editingStatus.id, requestData);
      } else {
        await mockInventoryService.createStatus(requestData);
      }

      await loadData();
      setShowForm(false);
      setEditingStatus(null);
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      console.error('Error saving inventory status:', error);
      swalError('Failed to save status. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackClick = () => {
    setShowForm(false);
    setEditingStatus(null);
    setFormData(initialFormData);
    setErrors({});
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Inventory Statuses Management</Modal.Header>
      <Modal.Body className="!p-0 overflow-hidden">
        <div className="modal-content">
          {!showForm ? (
            <div className="list-view">
              <div className="list-header">
                <div className="list-title">
                  <h3>Manage Inventory Statuses</h3>
                  <p>Define lifecycle statuses for tracking inventory item states</p>
                </div>
                <button
                  className="add-button"
                  onClick={handleAddClick}
                  disabled={loading}
                  data-testid="BTN_inventory_status_add"
                >
                  <span>+</span>
                  Add New Status
                </button>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading inventory statuses...</p>
                </div>
              ) : statuses.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">⚡</div>
                  <h3>No Statuses Found</h3>
                  <p>Start by creating your first inventory status to track item lifecycle.</p>
                  <button className="create-button" onClick={handleAddClick} data-testid="BTN_inventory_status_create_empty">
                    Create First Status
                  </button>
                </div>
              ) : (
                <div className="items-grid">
                  {statuses.map((status) => (
                    <div key={status.id} className="item-card">
                      {/* Header Row: Code + Name */}
                      <div className="data_card_header">
                        <div className="code-name-row">
                          <span className="item-code">{status.status_code || 'NO-CODE'}</span>
                          <span className="item-name">{status.status_name || 'NO-NAME'}</span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="data_default">
                        {status.status_description || 'No description provided'}
                      </div>

                      {/* Tags and Status */}
                      <div className="data_tags">
                        <span>Status: {status.is_active ? 'Active' : 'Inactive'}</span>
                        <span> • </span>
                        <span>State: {status.is_active_state ? 'Active' : 'Inactive'}</span>
                        <span> • </span>
                        <span>Order: {status.display_order}</span>
                        {status.status_color && (
                          <>
                            <span> • </span>
                            <span>Color: {status.status_color}</span>
                          </>
                        )}
                      </div>

                      {/* Footer with Actions */}
                      <div className="data_card_footer">
                        <div className="data_paths">
                          🎯 Status: {status.status_code}
                        </div>
                        <div style={{display: 'flex', gap: '8px', marginLeft: 'auto'}}>
                          <button
                            className="edit-button"
                            onClick={() => handleEditClick(status)}
                            disabled={submitting}
                            data-testid={`BTN_inventory_status_row_${status.id}_edit`}
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteClick(status)}
                            disabled={submitting}
                            data-testid={`BTN_inventory_status_row_${status.id}_delete`}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="form-view">
              <div className="form-header">
                <h3>{editingStatus ? 'Edit Status' : 'Add New Status'}</h3>
                <button className="back-button" onClick={handleBackClick} data-testid="BTN_inventory_status_back">
                  ← Back to List
                </button>
              </div>

              <form onSubmit={handleSubmit} className="inventory-form" data-testid="MOD_inventory_status_form">
                <div className="form-grid">
                  <div className="form-group">
                    <FormControl.Input
                      label="Status Code"
                      required
                      id="status_code"
                      name="status_code"
                      data-testid="INP_inventory_status_code"
                      value={formData.status_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, status_code: e.target.value }))}
                      error={errors.status_code}
                      disabled={submitting}
                      placeholder="e.g., ACTIVE, DEPRECATED, PLANNED"
                      maxLength={50}
                      helperText="Unique code identifier for this status"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Status Name"
                      required
                      id="status_name"
                      name="status_name"
                      data-testid="INP_inventory_status_name"
                      value={formData.status_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, status_name: e.target.value }))}
                      error={errors.status_name}
                      disabled={submitting}
                      placeholder="e.g., Active, Deprecated, In Planning"
                      maxLength={100}
                      helperText="Enter a descriptive name for this status"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Status Color"
                      id="status_color"
                      name="status_color"
                      type="color"
                      data-testid="INP_inventory_status_color"
                      value={formData.status_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, status_color: e.target.value }))}
                      error={errors.status_color}
                      disabled={submitting}
                      helperText="Color for visual identification"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Display Order"
                      required
                      id="display_order"
                      name="display_order"
                      type="number"
                      data-testid="INP_inventory_status_display_order"
                      min={1}
                      value={formData.display_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 1 }))}
                      error={errors.display_order}
                      disabled={submitting}
                      helperText="Order for displaying this status"
                    />
                  </div>

                  <div className="form-group full-width">
                    <FormControl.Textarea
                      label="Description"
                      required
                      id="status_description"
                      name="status_description"
                      data-testid="INP_inventory_status_description"
                      value={formData.status_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, status_description: e.target.value }))}
                      error={errors.status_description}
                      disabled={submitting}
                      rows={4}
                      placeholder="Describe what this status represents and its purpose..."
                      maxLength={500}
                      helperText="Detailed description of this status and when to use it"
                    />
                  </div>

                  <div className="form-group full-width">
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <FormControl.Checkbox
                        label="Active State (Items with this status are considered active)"
                        checked={formData.is_active_state}
                        onChange={(checked) => setFormData(prev => ({ ...prev, is_active_state: checked }))}
                        disabled={submitting}
                        data-testid="CHK_inventory_status_is_active_state"
                      />

                      <FormControl.Checkbox
                        label="Available for Use"
                        checked={formData.is_active}
                        onChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        disabled={submitting}
                        data-testid="CHK_inventory_status_is_active"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <Button variant="outline" type="button" onClick={handleBackClick} data-testid="BTN_inventory_status_cancel">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant={editingStatus ? 'update' : 'fill'}
                    icon={editingStatus ? Check : Save}
                    disabled={submitting}
                    data-testid="BTN_inventory_status_submit"
                  >
                    {submitting ? (editingStatus ? 'Updating...' : 'Submitting...') : editingStatus ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default InventoryStatusesModal;
